import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Badge,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FaPlus, FaEdit, FaTrash, FaMoneyBillWave, FaSearch } from "react-icons/fa";
import tiposValorizacionHoteleriaApi from "../../../services/api/tiposValorizacionHoteleriaApi";
import "react-toastify/dist/ReactToastify.css";

const emptyForm = {
  codigo: "",
  nombre: "",
  costo_hombre_dia: "",
  orden: 0,
  activo: true,
};

const MantenimientoTiposValorizacionHoteleria = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tiposValorizacionHoteleriaApi.list(false);
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error al cargar tarifas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.codigo || "").toLowerCase().includes(q) ||
        String(r.nombre || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openCreate = () => {
    setEditRow(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      codigo: row.codigo || "",
      nombre: row.nombre || "",
      costo_hombre_dia: row.costo_hombre_dia ?? "",
      orden: row.orden ?? 0,
      activo: Boolean(row.activo),
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) {
      toast.error("Código y nombre son obligatorios");
      return;
    }
    const costo = Number(form.costo_hombre_dia);
    if (Number.isNaN(costo) || costo < 0) {
      toast.error("Costo hombre/día inválido");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        codigo: form.codigo.trim(),
        nombre: form.nombre.trim(),
        costo_hombre_dia: costo,
        orden: Number(form.orden) || 0,
        activo: Boolean(form.activo),
      };
      if (editRow) {
        await tiposValorizacionHoteleriaApi.update(editRow.id, payload);
        toast.success("Tarifa actualizada");
      } else {
        await tiposValorizacionHoteleriaApi.create(payload);
        toast.success("Tarifa creada");
      }
      setShowModal(false);
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await tiposValorizacionHoteleriaApi.remove(toDelete.id);
      toast.success("Tarifa eliminada");
      setShowDelete(false);
      setToDelete(null);
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dh-reporte-bg">
      <style>{`
        .dh-reporte-bg {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          min-height: 100vh;
          padding-bottom: 2rem;
        }
        .dh-main-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem 0;
          margin-bottom: 2rem;
          border-radius: 0 0 30px 30px;
        }
        .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
        .dh-main-subtitle { opacity: 0.9; margin-top: 0.5rem; }
        .dh-card { border: none; border-radius: 20px; box-shadow: 0 15px 35px rgba(0,0,0,0.1); }
        .dh-card-header {
          border-radius: 20px 20px 0 0; padding: 1.25rem 1.5rem; border: none;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; font-weight: 700;
        }
        .btn-dh-primary {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .btn-dh-secondary {
          background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .dh-form-label { font-weight: 600; }
        .dh-form-control { border-radius: 10px; }
        .dh-alert-info {
          background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
          border: 1px solid #93c5fd; border-radius: 12px; padding: 1rem 1.25rem; color: #1e40af;
        }
      `}</style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaMoneyBillWave className="me-2" />
            Tarifas Valorización Hotelería
          </h1>
          <p className="dh-main-subtitle">
            Catálogo de costos hombre/día. Se asignan a cada módulo.
          </p>
        </Container>
      </div>

      <Container>
        <div className="dh-alert dh-alert-info mb-4">
          Todas las camas de un módulo heredan la tarifa del módulo. Edite aquí el
          costo y luego asígnela en <strong>Módulos</strong>.
        </div>

        <Card className="dh-card">
          <Card.Header className="dh-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>Tarifas</span>
            <Button className="btn-dh-primary" onClick={openCreate}>
              <FaPlus className="me-2" /> Nueva tarifa
            </Button>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label className="dh-form-label">
                  <FaSearch className="me-1" /> Buscar
                </Form.Label>
                <Form.Control
                  className="dh-form-control"
                  placeholder="Código o nombre..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Orden</th>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Costo hombre/día</th>
                    <th>Estado</th>
                    <th style={{ width: 140 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">
                        Sin tarifas
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r) => (
                      <tr key={r.id}>
                        <td>{r.orden}</td>
                        <td>
                          <code>{r.codigo}</code>
                        </td>
                        <td>{r.nombre}</td>
                        <td>
                          {Number(r.costo_hombre_dia).toLocaleString("es-PE", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td>
                          <Badge bg={r.activo ? "success" : "secondary"}>
                            {r.activo ? "Activo" : "Inactivo"}
                          </Badge>
                        </td>
                        <td className="d-flex gap-2">
                          <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                              setToDelete(r);
                              setShowDelete(true);
                            }}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>

      <Modal show={showModal} onHide={() => !saving && setShowModal(false)} centered>
        <Modal.Header
          closeButton={!saving}
          className="text-white"
          style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
        >
          <Modal.Title>{editRow ? "Editar tarifa" : "Nueva tarifa"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="dh-form-label">Código *</Form.Label>
              <Form.Control
                className="dh-form-control"
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                placeholder="C. WORKER"
              />
            </Col>
            <Col md={6}>
              <Form.Label className="dh-form-label">Orden</Form.Label>
              <Form.Control
                type="number"
                className="dh-form-control"
                value={form.orden}
                onChange={(e) => setForm({ ...form, orden: e.target.value })}
              />
            </Col>
            <Col xs={12}>
              <Form.Label className="dh-form-label">Nombre *</Form.Label>
              <Form.Control
                className="dh-form-control"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </Col>
            <Col md={6}>
              <Form.Label className="dh-form-label">Costo hombre/día *</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                min="0"
                className="dh-form-control"
                value={form.costo_hombre_dia}
                onChange={(e) =>
                  setForm({ ...form, costo_hombre_dia: e.target.value })
                }
              />
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="activo-tarifa"
                label="Activo"
                checked={form.activo}
                onChange={(e) => setForm({ ...form, activo: e.target.checked })}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button className="btn-dh-secondary" disabled={saving} onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button className="btn-dh-primary" disabled={saving} onClick={save}>
            {saving ? <Spinner size="sm" /> : "Guardar"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDelete} onHide={() => !saving && setShowDelete(false)} centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Eliminar tarifa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar <strong>{toDelete?.codigo}</strong>? Solo es posible si ningún
          módulo la usa.
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-dh-secondary" disabled={saving} onClick={() => setShowDelete(false)}>
            Cancelar
          </Button>
          <Button variant="danger" disabled={saving} onClick={confirmDelete}>
            {saving ? <Spinner size="sm" /> : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default MantenimientoTiposValorizacionHoteleria;
